import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { formatDateOnlyShort } from '@/modules/service-requests/shared/application-utils';
import { readBankInfo } from '@/modules/service-requests/shared/bank-info';
import { StatusBadge } from '@/modules/service-requests/shared/components/status-badge';
import { asksMaternityBenefit, genderLabelKey, typeLabel } from '../../utils/metlife-rules';
import { MetlifeApplication } from '../../types/metlife.types';

interface MetlifeDetailsDialogProps {
  application: MetlifeApplication | null;
  onOpenChange: (open: boolean) => void;
}

export const MetlifeDetailsDialog = ({ application, onOpenChange }: MetlifeDetailsDialogProps) => {
  const { t } = useTranslation();
  const bank = readBankInfo(application?.BankInfoJson);
  const maternity =
    application?.WantsMaternityBenefit === true
      ? t('YES')
      : application?.WantsMaternityBenefit === false
        ? t('NO')
        : undefined;

  const rows: [string, string | undefined][] = application
    ? [
        ['TYPE_OF_APPLICATION', typeLabel(application.TypeOfApplication)],
        ['METLIFE_ID_NUMBER', application.MetLifeIdNumber],
        ['EMPLOYEE_ID', application.EmployeeId],
        ['NAME', application.Name],
        ['POLICY_APPLICANT_NAME', application.PolicyApplicantName],
        ['EMAIL', application.Email],
        ['DESIGNATION', application.Designation],
        ['POLICY_APPLICANT_DATE_OF_BIRTH', formatDateOnlyShort(application.DateOfBirth)],
        ['POLICY_APPLICANT_GENDER', application.Gender && t(genderLabelKey(application.Gender))],
        ...(asksMaternityBenefit(application.TypeOfApplication, application.Gender)
          ? ([['WANTS_MATERNITY_BENEFIT', maternity]] as [string, string | undefined][])
          : []),
        ['MOBILE_NUMBER', application.MobileNumber],
        ['DEPARTMENT', application.RequestDepartmentName || application.BusinessUnitName],
        ['PRIME_BANK_ACCOUNT', bank.AccountNumber],
        ['BRANCH_NAME', bank.BranchName],
        ['ROUTING_NO', bank.RoutingNumber],
      ]
    : [];

  return (
    <Dialog open={Boolean(application)} onOpenChange={onOpenChange}>
      <DialogContent
        alignCloseWithTitle
        className="grid-cols-[minmax(0,1fr)] max-h-[90dvh] overflow-y-auto sm:max-w-lg"
      >
        <DialogHeader className="pr-6">
          <DialogTitle className="break-words">{t('METLIFE_APPLICATION_DETAILS')}</DialogTitle>
        </DialogHeader>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-[auto_minmax(0,1fr)] sm:gap-y-2">
          {rows.map(([labelKey, value]) => (
            <div key={labelKey} className="flex flex-col gap-0.5 sm:contents">
              <dt className="text-muted-foreground">{t(labelKey)}</dt>
              <dd className="min-w-0 break-words font-medium">{value || '-'}</dd>
            </div>
          ))}
          {application && (
            <div className="flex flex-col gap-0.5 sm:contents">
              <dt className="text-muted-foreground">{t('STATUS')}</dt>
              <dd>
                <StatusBadge status={application.Status} />
              </dd>
            </div>
          )}
          {application?.ApprovalStatusReason && (
            <div className="flex flex-col gap-0.5 sm:contents">
              <dt className="text-muted-foreground">{t('REASON')}</dt>
              <dd className="min-w-0 break-words font-medium">
                {application.ApprovalStatusReason}
              </dd>
            </div>
          )}
        </dl>
      </DialogContent>
    </Dialog>
  );
};
